import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { environment } from '@env/environment';
import { from, Observable, of, Subscription } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { nombreLegibleDeUrl } from '@data/utils/descarga-archivo.util';

interface UploadingFile {
  id: string;
  name: string;
  size: number;
  progress: number;
  status: 'uploading' | 'error';
  error?: string;
  file: File;
}

interface ApiResponse {
  error: boolean;
  msg: string;
  data?: any;
}

@Component({
  selector: 'app-input-multi-file',
  templateUrl: './input-multi-file.component.html',
  styleUrls: ['./input-multi-file.component.scss'],
  providers: [MessageService]
})
export class InputMultiFileComponent implements OnChanges {
  @Input() documentName = '';
  @Input() tooltip = 'Seleccionar archivos';
  @Input() folder = '';
  @Input() label = '';
  @Input() id = '';
  @Input() fileUrlValues: string[] = [];
  @Input() disabled = false;
  @Input() acceptedFormats = 'image/*,.pdf,.docx,.xlsx,.txt,.csv,.xml';
  @Input() maxFileSize = 100 * 1024 * 1024; // 100MB
  @Input() maxFiles = 20;

  @Output() filesChanged = new EventEmitter<string[]>();
  @Output() downloadRequested = new EventEmitter<string>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  isDragOver = false;
  uploadingFiles: UploadingFile[] = [];
  internalFiles: string[] = [];

  previewVisible = false;
  previewFileUrl: string | null = null;
  previewFileName: string | null = null;

  private rateLimited = false;
  private uploadSub: Subscription | null = null;

  constructor(
    private http: HttpClient,
    private messageService: MessageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fileUrlValues']) {
      this.internalFiles = Array.isArray(this.fileUrlValues)
        ? [...this.fileUrlValues.filter(url => !!url)]
        : [];
    }
  }

  get totalCount(): number {
    return this.internalFiles.length + this.uploadingFiles.length;
  }

  get canAddMore(): boolean {
    return !this.disabled && this.totalCount < this.maxFiles;
  }

  trackByUploadId(_index: number, item: UploadingFile): string {
    return item.id;
  }

  triggerFileInput(): void {
    if (!this.canAddMore) return;
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processFiles(Array.from(input.files));
      input.value = '';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.canAddMore) {
      this.isDragOver = true;
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    if (!this.canAddMore) return;

    const items = event.dataTransfer?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry?.isDirectory) {
          this.messageService.add({
            severity: 'warn',
            summary: 'No soportado',
            detail: 'No se permiten carpetas. Selecciona archivos individuales.'
          });
          return;
        }
      }
    }

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFiles(Array.from(files));
    }
  }

  onPreview(fileUrl: string): void {
    this.previewFileUrl = fileUrl;
    this.previewFileName = this.getFileNameFromUrl(fileUrl);
    this.previewVisible = true;
  }

  dismissUpload(id: string): void {
    this.uploadingFiles = this.uploadingFiles.filter(u => u.id !== id);
  }

  deleteFile(fileUrl: string): void {
    if (this.disabled) return;

    this.messageService.clear();
    this.messageService.add({
      key: 'confirm-delete-multi',
      sticky: true,
      severity: 'warn',
      summary: 'Confirmar eliminación',
      detail: '¿Estás seguro de que deseas eliminar este archivo?',
      data: { fileUrl }
    });
  }

  confirmDelete(fileUrl: string): void {
    const encodedFileUrl = encodeURIComponent(fileUrl);
    const deleteUrl = `${environment.apiUrl}documents/delete?fileUrl=${encodedFileUrl}`;

    this.http.delete(deleteUrl)
      .pipe(
        catchError(() => {
          this.messageService.clear();
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al eliminar el archivo.'
          });
          return of(null);
        })
      )
      .subscribe(response => {
        if (response !== null) {
          this.internalFiles = this.internalFiles.filter(url => url !== fileUrl);
          this.filesChanged.emit([...this.internalFiles]);
          this.messageService.clear();
        }
      });
  }

  cancelDelete(): void {
    this.messageService.clear();
  }

  onDownloadFile(fileUrl: string): void {
    this.downloadRequested.emit(fileUrl);
  }

  getFileNameFromUrl(url: string): string {
    return nombreLegibleDeUrl(url);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return 'fa-file-pdf';
      case 'doc': case 'docx': return 'fa-file-word';
      case 'xls': case 'xlsx': return 'fa-file-excel';
      case 'jpg': case 'jpeg': case 'png': case 'gif': case 'webp': return 'fa-file-image';
      case 'txt': case 'csv': return 'fa-file-lines';
      default: return 'fa-file';
    }
  }

  getTruncatedFileName(fileName: string, maxLength: number = 25): string {
    if (fileName.length <= maxLength) return fileName;

    const extension = fileName.split('.').pop();
    const nameWithoutExtension = fileName.substring(0, fileName.lastIndexOf('.'));
    const maxNameLength = maxLength - (extension ? extension.length + 4 : 3);

    return extension
      ? `${nameWithoutExtension.substring(0, maxNameLength)}...${extension}`
      : `${fileName.substring(0, maxLength - 3)}...`;
  }

  private processFiles(files: File[]): void {
    const available = this.maxFiles - this.totalCount;
    if (available <= 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Límite alcanzado',
        detail: `Solo se permiten hasta ${this.maxFiles} archivos.`
      });
      return;
    }

    let candidates = files;
    if (candidates.length > available) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Límite parcial',
        detail: `Se procesarán solo los primeros ${available} archivo(s); el resto excede el máximo de ${this.maxFiles}.`
      });
      candidates = candidates.slice(0, available);
    }

    //* A propósito compara contra el nombre CRUDO del blob, no contra el legible: desde
    //* ADR-0033 el blob lleva un token único delante, así que este candado no bloquea dos
    //* archivos con el mismo nombre — y ADR-0033 existe justamente para permitirlos. Usar
    //* aquí el nombre legible lo reactivaría, que es un cambio de comportamiento aparte.
    const nombreCrudo = (url: string) => url.substring(url.lastIndexOf('/') + 1).split('?')[0];
    const existingNames = new Set<string>([
      ...this.internalFiles.map(nombreCrudo),
      ...this.uploadingFiles.map(u => u.name)
    ]);

    const valid: File[] = [];
    for (const f of candidates) {
      if (existingNames.has(f.name)) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Archivo duplicado',
          detail: `Ya existe un archivo con el nombre "${f.name}".`
        });
        continue;
      }
      if (!this.isValidFileType(f)) {
        this.messageService.add({
          severity: 'error',
          summary: 'Tipo no permitido',
          detail: `"${f.name}" no es un tipo permitido. Acepta: PDF, DOCX, XLSX, imágenes, TXT, CSV, XML.`
        });
        continue;
      }
      if (f.size > this.maxFileSize) {
        this.messageService.add({
          severity: 'error',
          summary: 'Archivo muy grande',
          detail: `"${f.name}" excede el máximo de ${Math.round(this.maxFileSize / (1024 * 1024))}MB.`
        });
        continue;
      }
      valid.push(f);
      existingNames.add(f.name);
    }

    if (valid.length === 0) return;

    const queued = valid.map<UploadingFile>(file => ({
      id: this.makeId(),
      name: file.name,
      size: file.size,
      progress: 0,
      status: 'uploading',
      file
    }));

    this.uploadingFiles = [...this.uploadingFiles, ...queued];

    const concurrency = this.rateLimited ? 1 : 3;
    this.uploadSub?.unsubscribe();
    this.uploadSub = from(queued)
      .pipe(
        mergeMap(item => this.uploadOne(item).pipe(catchError(() => of(null))), concurrency)
      )
      .subscribe();
  }

  private uploadOne(item: UploadingFile): Observable<void> {
    const formData = new FormData();
    formData.append('file', item.file);
    formData.append('id', this.id);
    formData.append('documentName', this.documentName);
    formData.append('folderName', this.folder);

    const url = `${environment.apiUrl}documents/upload`;

    return new Observable<void>(subscriber => {
      const sub = this.http.post<ApiResponse>(url, formData, {
        reportProgress: true,
        observe: 'events'
      }).subscribe({
        next: (event: HttpEvent<ApiResponse>) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            const pct = Math.round((event.loaded / event.total) * 100);
            this.updateUploading(item.id, { progress: Math.min(pct, 99) });
          } else if (event.type === HttpEventType.Response) {
            const body = event.body;
            if (body && body.data && body.data.fileUrl) {
              this.completeUpload(item.id, body.data.fileUrl);
            } else {
              this.failUpload(item.id, body?.msg || 'No se pudo subir el archivo.');
            }
            subscriber.next();
            subscriber.complete();
          }
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 429) {
            this.rateLimited = true;
          }
          const msg = err?.error?.msg || err?.message || 'No se pudo subir el archivo.';
          this.failUpload(item.id, msg);
          subscriber.error(err);
        }
      });
      return () => sub.unsubscribe();
    });
  }

  private updateUploading(id: string, patch: Partial<UploadingFile>): void {
    this.uploadingFiles = this.uploadingFiles.map(u =>
      u.id === id ? { ...u, ...patch } : u
    );
  }

  private completeUpload(id: string, fileUrl: string): void {
    this.uploadingFiles = this.uploadingFiles.filter(u => u.id !== id);
    this.internalFiles = [...this.internalFiles, fileUrl];
    this.filesChanged.emit([...this.internalFiles]);
  }

  private failUpload(id: string, error: string): void {
    this.uploadingFiles = this.uploadingFiles.map(u =>
      u.id === id ? { ...u, status: 'error', error, progress: 0 } : u
    );
    this.messageService.add({
      severity: 'error',
      summary: 'Error al subir',
      detail: error
    });
  }

  private isValidFileType(file: File): boolean {
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/xml',
      'text/xml',
      'text/plain',
      'text/csv'
    ];

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'docx', 'xlsx', 'txt', 'csv', 'xml'];

    return allowedTypes.includes(file.type) || (fileExtension ? allowedExtensions.includes(fileExtension) : false);
  }

  private makeId(): string {
    const c: any = (typeof crypto !== 'undefined') ? crypto : null;
    if (c?.randomUUID) return c.randomUUID();
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}
